import { type Socket, io } from 'socket.io-client';

import { getConfig } from '../config';
import { SocketEvent } from '../entity/api/routes/socket';
import { InternalServerError, UnauthorizedError } from '../error';
import { addTrackCrumb, trackError } from '../error/tracking';
import { captureException } from '../logger/capture-exception';
import { type SocketFn, getSocketEventMap, handleStatusError } from './socket.router';

const RECONNECT_WAIT_TIME = 1000;
const TIMEOUT_TIME = 2000;
const MAX_RETRIES = 50;

let socket: Socket<ReturnType<typeof getSocketEventMap>> | null | undefined = undefined;
let socketToken: string | null | undefined = undefined;

function addSocketEvents(socketBase: typeof socket) {
  if (socketBase == null) {
    throw new InternalServerError("socket wasn't set right");
  }

  // lets setup all the socket events we require for the app
  const eventMap = getSocketEventMap();
  const events = Object.keys(eventMap);
  for (let i = 0; i < events.length; i += 1) {
    const evt = events[i] as SocketEvent;
    // TODO: should improve upon this typing
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    const fn = (eventMap as any)[evt] as SocketFn;

    // listen to the event
    // TODO: investigate why this is happening
    socketBase.on(evt as any, function (data: unknown) {
      const parsedData: unknown = typeof data === 'string' ? JSON.parse(data) : data;
      if (getConfig().env !== 'production') {
        // eslint-disable-next-line no-console
        console.log('- socket event incoming', evt, parsedData);
      }

      // do not go through if invalid
      if (typeof parsedData !== 'object' || parsedData == null) {
        return Promise.resolve(undefined);
      }

      return fn(parsedData as { [key: string]: unknown }).catch(function (err) {
        captureException(
          err,
          {
            file: 'socket.ts',
            method: 'fn',
            infoMessage: `unable to proceed with socket fn ${evt}`,
          },
          trackError
        );
      });
    });
  }
}

export function unsetSocket(socketToBe?: typeof socket) {
  addTrackCrumb({
    category: 'auth',
    message: 'unsetting socket',
    level: 'info',
  });

  const handleSocket = socketToBe ?? socket;

  if (handleSocket === socket) {
    socketToken = null;
    socket = null;
  }

  // no need to set a new socket if we have one already
  if (handleSocket == null) {
    return;
  }

  try {
    handleSocket.off('connect_error');
    handleSocket.off('error' as any);
    handleSocket.off('disconnect');
    handleSocket.off('reconnect_failed' as any);
    handleSocket.off('reconnect_error' as any);

    handleSocket.disconnect();
  } catch (_err) {
    // no need to handle the error on disconnect
  }
}

async function newSocket(getterToken?: () => Promise<string> | null, currentRetries = 0) {
  const token = getterToken == null ? null : await getterToken();

  addTrackCrumb({
    category: 'auth',
    message: `setting new socket: ${token}`,
    level: 'info',
  });

  if (token == null || token.length === 0) {
    return Promise.reject(new UnauthorizedError('token is required to setup the socket'));
  }

  const socketToBe = io(getConfig().api, {
    path: '/ws',
    transports: ['websocket'],
    auth: { token },
    reconnection: false,
  });

  return new Promise<Socket>(function (resolve, _reject) {
    let isResolved = false;
    let timeout: undefined | ReturnType<typeof setTimeout> = undefined;
    let retries = currentRetries;

    function onPong() {
      if (isResolved) {
        return;
      }
      isResolved = true;

      if (timeout != null) {
        clearTimeout(timeout);
      }

      socketToBe.off('connect');
      socketToBe.off('connect_error');
      socketToBe.off('error');
      socketToBe.off(SocketEvent.STATUS_PONG, onPong);

      // eslint-disable-next-line no-console
      console.log('WS: Socket connected');
      retries = 0;
      resolve(socketToBe);
    }

    function onError(err: unknown) {
      retries++;
      if (retries > MAX_RETRIES) {
        if (socketToBe.connected) {
          socketToBe.disconnect();
        }
        // Call error handler directly instead of emitting socket event
        handleStatusError().catch(function (err) {
          captureException(
            err,
            {
              file: 'sdk/socket.ts',
              method: 'newSocket',
              infoMessage: 'unable to handle status error',
            },
            trackError
          );
        });
        return;
      }

      if (isResolved) {
        return;
      }
      isResolved = true;

      if (timeout != null) {
        clearTimeout(timeout);
      }

      socketToBe.disconnect();
      socketToBe.off(SocketEvent.STATUS_PONG, onPong);

      captureException(
        err,
        {
          file: 'sdk/socket.ts',
          method: 'newSocket',
          infoMessage: 'unable to setup socket',
        },
        trackError
      );

      new Promise(function (timerResolve) {
        setTimeout(function () {
          timerResolve(undefined);
        }, RECONNECT_WAIT_TIME);
      })
        .then(function () {
          // socket is always required
          // restart connection upon timeout
          resolve(newSocket(getterToken, retries));
        })
        .catch(function (_err) {
          // NOTE: there is no actual error
        });
    }

    // connection might have timed out so we handle it as an error and reconnect
    timeout = setTimeout(function () {
      onError(new InternalServerError('connect to socket timed out', { info: { token } }));
    }, TIMEOUT_TIME);

    socketToBe.on('connect', function () {
      // keep sending pings until a pong comes
      const pingInterval = setInterval(function () {
        socketToBe.emit(SocketEvent.STATUS_PING, {});
      }, 500);

      socketToBe.once(SocketEvent.STATUS_PONG, function () {
        clearInterval(pingInterval);
        onPong();
      });
    });

    socketToBe.on('connect_error', onError);
    socketToBe.on('error', onError);
  });
}

export async function setSocket(getterToken?: () => Promise<string> | null): Promise<void> {
  const token = getterToken == null ? undefined : await getterToken();
  addTrackCrumb({
    category: 'auth',
    message: `setting socket with token ${token}`,
    level: 'info',
  });

  // don't go any further it is probably unauth
  if (token == null || token.length === 0) {
    unsetSocket();
    return;
  }

  // no point in asking a socket for the same token
  if (token == socketToken && socket != null) {
    return;
  }

  function retrySetSocket() {
    unsetSocket();

    return new Promise<Awaited<ReturnType<typeof setSocket>>>(function (resolve, reject) {
      setTimeout(function () {
        setSocket(getterToken).then(resolve).catch(reject);
      }, RECONNECT_WAIT_TIME);
    });
  }

  function handleError() {
    unsetSocket();

    retrySetSocket().catch(function (err) {
      captureException(
        err,
        {
          file: 'sdk/socket.ts',
          method: 'setSocket',
          infoMessage: 'unable to setup socket',
        },
        trackError
      );
    });
  }

  try {
    unsetSocket();

    const newSock = await newSocket(getterToken);
    socket = newSock;
    socketToken = token;

    newSock.on('connect_error', handleError);
    newSock.on('error', handleError);
    newSock.on('disconnect', handleError);
    newSock.on('reconnect_error', handleError);

    addSocketEvents(newSock);
  } catch (err) {
    captureException(
      err,
      {
        file: 'sdk/socket.ts',
        method: 'setSocket',
        infoMessage: 'unable to setup socket',
      },
      trackError
    );

    return retrySetSocket();
  }
}

export function hasSocket(): boolean {
  return socket != null;
}

function waitForSocket(): Promise<boolean> {
  return new Promise(function (resolve) {
    if (hasSocket()) {
      resolve(true);
      return;
    }

    const timer = setInterval(function () {
      if (!hasSocket()) {
        return;
      }

      resolve(true);
      clearInterval(timer);
    }, 500);
  });
}

export async function getSocket(wait: boolean) {
  if (wait) {
    await waitForSocket();
  }

  return socket;
}
